'use client';
import { readCookieHeader, writePreferenceCookie } from './cookies';
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useConfig, useSkinCatalogue } from './queries';
import {
  DEFAULT_SKIN, allowedSkinChoices, builtinCatalogue, catalogueEntry, currentSkinChoice, isSkinId,
  nextSkinChoice, resolveSkin, skinFamily, skinStylesheetUrl, type SkinCatalogue,
} from './skins';

const STORAGE_KEY = 'elowen-skin';

function readSkinCookie(): string | null {
  const value = readCookieHeader(document.cookie, STORAGE_KEY);
  return value && isSkinId(value.toLowerCase()) ? value.toLowerCase() : null;
}

/** Load one customer skin's stylesheet, resolving when the browser has it. The link is marked with the skin
 *  id so the one the server already rendered for the document's initial design is found rather than fetched
 *  twice.
 *
 *  The load must be awaited BEFORE the attributes change: `data-skin` alone decides which rules match, and a
 *  custom skin's sheet is the only place its palette exists — switching first would paint the document with
 *  the base palette for the length of a round trip. */
function loadSkinSheet(id: string, url: string): Promise<void> {
  const existing = document.querySelector<HTMLLinkElement>(`link[data-skin-sheet="${id}"]`);
  if (existing && existing.href.endsWith(url)) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const link = existing ?? document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    link.dataset.skinSheet = id;
    link.addEventListener('load', () => { resolve(); }, { once: true });
    link.addEventListener('error', () => { link.remove(); reject(new Error(`skin stylesheet ${url} failed to load`)); }, { once: true });
    if (!existing) document.head.appendChild(link);
  });
}

interface SkinContextValue {
  /** The account's current choice, or null when the visible design is the operator's default and that
   *  default is not itself one of the offered choices. */
  choice: string | null;
  /** The skin the document is actually WEARING — exactly what `data-skin` says, and never nothing: every
   *  resolution ends at a design this instance has (DEFAULT_SKIN is the floor). It is not the same thing as
   *  `choice`: an operator who sets ELOWEN_SKIN without offering it in the allow-list gives everyone that
   *  design with nothing chosen. Anything deciding what to RENDER for the active design has to read this
   *  one — reading `choice` would mount one design's shell inside another design's stylesheet. */
  skin: string;
  /** What may be picked. Empty means the instance has not enabled switching at all. */
  allowed: string[];
  /** The catalogue this context resolves against, for anything that needs to name or describe a skin. */
  catalogue: SkinCatalogue;
  /** True while the chosen design's stylesheet is still loading; the visible design has not changed yet. */
  switching: boolean;
  /** Advance to the next allowed choice — the switcher's whole interaction. Async because a customer skin's
   *  sheet has to be in the document before the attribute that selects it is. */
  cycle: () => Promise<void>;
}

const SkinContext = createContext<SkinContextValue | null>(null);

/** THE ONE WRITER of what the document wears. For a built-in that is the whole mechanism: its CSS is already
 *  in the page, scoped under its own `[data-skin]`, so the attribute alone decides which rules match. A
 *  customer skin adds one thing — its stylesheet has to be loaded first, which `cycle` guarantees.
 *
 *  It takes the RESOLVED skin rather than the account's choice, because the resolution is what `data-skin`
 *  states: revoking the active skin from the allow-list, or moving the operator's ELOWEN_SKIN, changes the
 *  visible design while the page is open, and both land on another design the catalogue has — DEFAULT_SKIN
 *  when nothing else decides. The attribute is therefore never removed; there is no unattributed document to
 *  fall back to. Every path that can change the visible design goes through this function via the single
 *  effect below, so the attribute cannot disagree with the `skin` the context reports and the shell renders
 *  from.
 *
 *  The attributes are not quite the whole story, because they are not the only thing painting the canvas.
 *  The server writes an inline `background-color` onto <html> and <body> (app/layout.tsx) so the document has
 *  the right fill BEFORE the stylesheet is parsed — and an inline style outranks the `var(--color-background)`
 *  rule in base.css that would otherwise follow the skin. Left in place it freezes the canvas at whatever
 *  design the document was SERVED as: switching to a dark design repainted every surface while <html> stayed
 *  white underneath, which shows in the overscroll fill and in the browser's own chrome.
 *
 *  So the switch hands the canvas back to the cascade. Deleting the inline value is the whole fix, and it
 *  needs no second copy of any palette on the client: by the time anyone can press the switcher the
 *  stylesheet has long since landed, so `--color-background` is already the authority the anti-FOUC value was
 *  standing in for. The same reasoning covers the theme colour, which is read back from the resolved token
 *  rather than restated — the address bar and the task switcher then follow the design like everything else,
 *  instead of reporting the one the document happened to arrive in. */
function applySkin(skin: string, family: string, base: string): void {
  const root = document.documentElement;
  root.setAttribute('data-skin', skin);
  root.setAttribute('data-skin-family', family);
  root.setAttribute('data-skin-base', base);
  root.style.removeProperty('background-color');
  document.body?.style.removeProperty('background-color');
  const themeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (themeColor) themeColor.content = getComputedStyle(root).backgroundColor;
}

/** `catalogue` and the other three props are what the SERVER rendered this document with. Starting state
 *  must match them exactly or hydration would mismatch — and starting from anything else is what would make
 *  the interface repaint in another design a moment after it appeared. That is why the catalogue prop is
 *  the SEED of the live query rather than a value the provider only ever reads: adding or removing a skin
 *  in Settings has to reach a page that is already open, and everything below resolves against the
 *  refreshed catalogue. */
export function SkinProvider({
  children,
  catalogue: seed = builtinCatalogue(),
  initialChoice = null,
  allowedSkins,
  fallback = null,
}: {
  children: ReactNode;
  catalogue?: SkinCatalogue;
  initialChoice?: string | null;
  allowedSkins?: readonly string[];
  fallback?: string | null;
}) {
  const catalogue = useSkinCatalogue(seed);
  // The seed is what the SERVER could see, and a logged-out document sees nothing: the prefetch has no
  // session cookie to forward, so it returns null and the seed is empty. Without the live query, someone who
  // logs in without reloading would never be offered the switcher at all — the page they land on was
  // rendered before they had an identity. The query is not an extra request: the shell already reads the
  // same config for its toast durations.
  const config = useConfig();
  const allowed = useMemo(
    () => allowedSkinChoices(config.data?.allowedSkins ?? allowedSkins, catalogue),
    [config.data?.allowedSkins, allowedSkins, catalogue],
  );
  const [choice, setChoice] = useState<string | null>(initialChoice);
  const [switching, setSwitching] = useState(false);
  /** Monotonic request id: a slow stylesheet must not be able to land on top of a later choice. */
  const request = useRef(0);

  useEffect(() => {
    // localStorage remains the client's source of truth, so a session that predates the cookie — or one whose
    // cookie was cleared — still gets its stored choice back, and writing the cookie means the NEXT document
    // is already correct on the server. A stored choice the admin has since revoked is dropped here rather
    // than applied, which is the same rule the server used, so the two cannot disagree.
    //
    // Reconciled against the CURRENT choice, not against the one the server seeded: the allow-list is live
    // and can narrow and widen again while the page is open, and comparing to the seed would make the first
    // revocation permanent — re-allowing the skin would leave the dropped choice at null, because the
    // recomputed value happens to equal the seed again.
    const stored = localStorage.getItem(STORAGE_KEY);
    const resolved = currentSkinChoice(stored ?? readSkinCookie(), allowed, fallback, catalogue);
    setChoice((previous) => (previous === resolved ? previous : resolved));
    if (resolved) {
      if (stored !== resolved) localStorage.setItem(STORAGE_KEY, resolved);
      if (readSkinCookie() !== resolved) writePreferenceCookie(STORAGE_KEY, resolved);
    }
  }, [allowed, fallback, catalogue]);

  const cycle = useCallback(async () => {
    const next = nextSkinChoice(choice, allowed);
    if (!next) return;
    const entry = catalogueEntry(catalogue, next);
    const url = skinStylesheetUrl(entry);
    const ticket = ++request.current;
    try {
      if (url) {
        setSwitching(true);
        await loadSkinSheet(next, url);
      }
      if (request.current !== ticket) return; // a later choice has taken over
      setChoice(next);
      localStorage.setItem(STORAGE_KEY, next);
      writePreferenceCookie(STORAGE_KEY, next);
    } catch (error) {
      // The sheet could not be loaded, so the document keeps the design it is wearing and the stored choice
      // stays where it was. The reader sees only the button returning to idle (the design on screen is a real
      // one); the console carries the reason for whoever has to diagnose the missing sheet.
      console.error('Skin switch failed', error);
    } finally {
      if (request.current === ticket) setSwitching(false);
    }
  }, [allowed, catalogue, choice]);

  // The same resolution the SERVER ran to decide the attributes (app/layout.tsx), so the two cannot disagree
  // about which design is on screen.
  const skin = useMemo(() => resolveSkin(choice, allowed, fallback, catalogue), [choice, allowed, fallback, catalogue]);
  // The document follows that resolution and nothing else. Every way the visible design can change — first
  // mount, the switcher, an admin revoking the active skin, the operator's default moving — changes THIS
  // value and therefore lands here, so there is one place deciding what `<html>` wears and no path that can
  // leave a stale attribute behind. A layout effect, because React runs every descendant's effects before
  // this provider's: a passive effect here would let a consumer that reads resolved tokens when `skin`
  // changes (the interactive view copies them into its frame) read the previous design.
  useLayoutEffect(() => {
    applySkin(skin, skinFamily(skin, catalogue), catalogueEntry(catalogue, skin)?.base ?? DEFAULT_SKIN);
  }, [skin, catalogue]);
  const activeSheet = skinStylesheetUrl(catalogueEntry(catalogue, skin));
  useEffect(() => {
    if (activeSheet) void loadSkinSheet(skin, activeSheet).catch((error: unknown) => {
      console.error('Skin stylesheet refresh failed', error);
    });
  }, [skin, activeSheet]);

  const value = useMemo(
    () => ({ choice, skin, allowed, catalogue, switching, cycle }),
    [choice, skin, allowed, catalogue, switching, cycle],
  );
  return <SkinContext.Provider value={value}>{children}</SkinContext.Provider>;
}

/** Tolerates a missing provider (bare component tests, isolated mounts) by reporting nothing to switch — the
 *  switcher then renders nothing, which is also what an instance with switching disabled does. The skin it
 *  reports is DEFAULT_SKIN rather than "none", for the same reason the provider never removes the attribute:
 *  a component mounted outside the provider still has to render for a real design, and the one it would have
 *  got from a real document with nothing chosen is the default. */
export function useSkin(): SkinContextValue {
  return useContext(SkinContext) ?? {
    choice: null,
    skin: DEFAULT_SKIN,
    allowed: [],
    catalogue: builtinCatalogue(),
    switching: false,
    cycle: async () => {},
  };
}
